import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCategories, deleteCategory } from '../../store/slices/settingsSlice';
import LoadingSpinner from '../common/LoadingSpinner';
import AddEditCategoryDialog from './AddEditCategoryDialog';
import ConfirmDialog from '../common/ConfirmDialog';

const CategoriesTab = () => {
  const dispatch = useDispatch();
  const { categories, loading } = useSelector(state => state.settings);
  const { user: currentUser } = useSelector(state => state.auth);
  const [dialogMode, setDialogMode] = useState({ type: null, data: null });
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, item: null });

  const userRole = currentUser?.profile?.role;
  const hasEditPermission = ['super_admin', 'admin'].includes(userRole);

  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  const handleAdd = () => {
    if (!hasEditPermission) return;
    setDialogMode({ type: 'add', data: null });
  };

  const handleEdit = (category) => {
    if (!hasEditPermission) return;
    setDialogMode({ type: 'edit', data: category });
  };

  const handleDelete = (category) => {
    if (!hasEditPermission) return;
    setDeleteConfirm({ show: true, item: category });
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirm.item) {
      await dispatch(deleteCategory(deleteConfirm.item._id));
      setDeleteConfirm({ show: false, item: null });
      dispatch(fetchCategories());
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <div className="flex justify-between mb-4">
        <h3 className="text-lg font-medium">Categories ({categories.length})</h3>
        {hasEditPermission && (
          <button 
            onClick={handleAdd}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Add Category
          </button>
        )}
      </div>

      <div className="border rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <div className="overflow-y-auto max-h-[calc(100vh-300px)]">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {categories.map((category) => (
                  <tr key={category._id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {category.name}
                    </td>
                    <td className="px-6 py-4">
                      {category.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        category.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {category.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {hasEditPermission ? (
                        <div className="flex space-x-4">
                          <button
                            onClick={() => handleEdit(category)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(category)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-400">No actions available</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {hasEditPermission && (
        <>
          <AddEditCategoryDialog
            isOpen={!!dialogMode.type}
            mode={dialogMode.type}
            data={dialogMode.data}
            onClose={() => setDialogMode({ type: null, data: null })}
            onSuccess={() => {
              setDialogMode({ type: null, data: null });
              dispatch(fetchCategories());
            }}
          />

          <ConfirmDialog
            isOpen={deleteConfirm.show}
            onClose={() => setDeleteConfirm({ show: false, item: null })}
            onConfirm={handleConfirmDelete}
            title="Delete Category"
            message={`Are you sure you want to delete "${deleteConfirm.item?.name}"? This action cannot be undone.`}
          />
        </>
      )}
    </div>
  );
};

export default CategoriesTab; 